import { Component, Input, signal, effect, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ActivatedRoute, Router } from '@angular/router';

import {
  ExpansionPanelComponent,
  AccordionItem,
} from '../../webpagecreation/expansion-panel/expansion-panel.component';
import { DynamicPageCreationComponent } from '../../webpagecreation/dynamic-page-creation/dynamic-page-creation.component';
import { DynamicFileUploadComponent } from '../../webpagecreation/dynamic-file-upload/dynamic-file-upload.component';

import { AccordionPageService } from '../../../inputfileds/form_services/WebsiteManagement/accordian_page/accordion-page.service';
import { ApiservicesService } from '../../../Services/Apis/apiservices.service';
import { environment } from '../../../../environments/environment';
import { TranslateDirective } from '../../Utilites/Directives/translate.directive';
import { ToastService } from '../../../Services/toast/toast.service';

interface CreateAccordionPayload {
  title: string;
  description?: string;
  website_id: string;
  website_page_id: string;
  parent_accordion_id?: string;
  open_in_new_tab?: boolean;
  data?: { data_type_id: string }[];
}

@Component({
  selector: 'app-accordion-dynamic-page',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatCardModule,
    MatDialogModule,
    MatExpansionModule,
    MatIconModule,
    MatMenuModule,
    MatProgressSpinnerModule,
    ExpansionPanelComponent,
    TranslateDirective,
  ],
  templateUrl: './accordion-dynamic-page.component.html',
  styleUrls: ['./accordion-dynamic-page.component.scss'],
})
export class AccordionDynamicPageComponent implements OnInit {
  @Input() websiteId!: string;
  @Input() pageId!: string;
  @Input() apiEndpoint!: string;

  // ─── State ────────────────────────────────────────
  accordionItems = signal<AccordionItem[]>([]);
  expandedIds = new Set<string>();
  selectedId = signal<string | null>(null);
  isLoading = signal(false);

  constructor(
    private dialog: MatDialog,
    private accordionService: AccordionPageService,
    private api: ApiservicesService,
    private route: ActivatedRoute,
    private router: Router,
    private toast: ToastService,
  ) {}

  ngOnInit(): void {
    // Fallback to query params if inputs are not provided
    this.websiteId ||= this.route.snapshot.queryParams['websiteId'];
    this.pageId ||= this.route.snapshot.queryParams['pageId'];
    this.apiEndpoint ||= this.route.snapshot.queryParams['endpoint'];

    if (this.websiteId && this.pageId) {
      this.loadItems();
    }
  }

  // ─── Core Methods ─────────────────────────────────

  loadItems(silent = false): void {
    if (!silent) this.isLoading.set(true);

    this.api.getAccordionItems(this.websiteId, this.pageId).subscribe({
      next: (res: { rows: any[] }) => {
        const items = (res.rows || []).map((item) =>
          this.mapApiToAccordionItem(item),
        );
        this.accordionItems.set(items);
      },
      error: (err) => {
        console.error('[Accordion] Load failed', err);
        // TODO: show user-friendly error (snackbar/toast)
      },
      complete: () => this.isLoading.set(false),
    });
  }

  addAccordion(parent?: AccordionItem): void {
    const isNested = !!parent;

    const dialogRef = this.dialog.open(DynamicPageCreationComponent, {
      width: '640px',
      maxWidth: '92vw',
      maxHeight: '92vh',
      data: {
        pageTitle: isNested
          ? 'Add Nested Accordion Item'
          : 'Create New Accordion',
        questions$: this.accordionService.getCreateAccordionPage(),
        initialData: null,
        TabButton: true,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (!result?.title) return;

      const payload: CreateAccordionPayload = {
        title: result.title.trim(),
        description: result.description?.trim(),
        website_id: this.websiteId,
        website_page_id: this.pageId,
        open_in_new_tab: !!result.open_in_new_tab,
        data: (result.data ?? []).map((d: any) => ({
          data_type_id: String(d.id),
        })),
      };

      if (isNested && parent?.id) {
        payload.parent_accordion_id = parent.id;
        this.expandedIds.add(parent.id);
      }

      // Optimistic UI update
      const tempId = `temp-${Date.now()}`;
      const optimistic: AccordionItem = {
        id: tempId,
        title: result.title.trim() || 'New Item',
        description: result.description?.trim() || '',
        type: 'accordion',
        children: [],
        expanded: false,
        actions: true,
        actionTooltip: 'Item actions',
      };

      this.accordionItems.update((list) => {
        if (!parent) return [...list, optimistic];

        const insertRecursive = (items: AccordionItem[]): AccordionItem[] =>
          items.map((it) => {
            if (it.id === parent.id) {
              return {
                ...it,
                children: [...(it.children ?? []), optimistic],
              };
            }
            if (it.children?.length) {
              return { ...it, children: insertRecursive(it.children) };
            }
            return it;
          });

        return insertRecursive(list);
      });

      this.api.createAccordionPage(payload).subscribe({
        next: () => this.loadItems(true), // silent reload to get real IDs
        error: () => {
          // Rollback on failure (simple version: reload)
          this.loadItems();
        },
      });
    });
  }

  private mapApiToAccordionItem(api: any): AccordionItem {
    const children = (api.data ?? []).map((c: any) =>
      this.mapApiToAccordionItem(c),
    );

    const links = (api.accordion_content ?? []).map((c: any, idx: number) => ({
      id: c.accordion_details_id,
      label: c.type === 'FILES' ? `File ${idx + 1}` : `Link ${idx + 1}`,
      url: c.content || '#',
      description: `Type: ${c.type}`,
      icon: c.type === 'FILES' ? 'fas fa-file' : 'fas fa-link',
    }));

    const id = String(api.id);

    return {
      id,
      title: api.title || api.name || 'Untitled Accordion',
      description: api.description || '',
      type: 'accordion',
      expanded: this.expandedIds.has(id),
      actions: true,
      actionTooltip: 'Actions',
      children: children.length ? children : undefined,
      links: links.length ? links : undefined,
      data: api, // keep original for editing
    };
  }

  // ─── Event Handlers ───────────────────────────────

  onAction(event: { action: string; item: AccordionItem }): void {
    const { action, item } = event;

    switch (action) {
      case 'addAccordion':
        this.addAccordion(item);
        break;
      case 'uploadFile':
        this.openFileUpload(item);
        break;
      case 'link':
        this.openAddLinkDialog(item);
        break;
      case 'edit':
        this.openEditDialog(item);
        break;
      case 'delete':
        this.confirmDelete(item);
        break;
      default:
        console.warn('Unhandled action:', action);
    }
  }

  onItemExpanded(item: AccordionItem): void {
    if (item.id) {
      this.expandedIds.add(item.id);
      this.selectedId.set(item.id);
    }
  }

  onItemCollapsed(item: AccordionItem): void {
    item.id && this.expandedIds.delete(item.id);
  }

  goBack(): void {
    this.router.navigate(['/websites/WebcomponentList'], {
      queryParams: {
        websiteId: this.websiteId,
        pageId: this.pageId,
      },
      queryParamsHandling: 'merge',
    });
  }

  // ─── Dialogs ──────────────────────────────────────

  private openFileUpload(parent?: AccordionItem): void {
    const dialogRef = this.dialog.open(DynamicFileUploadComponent, {
      width: '520px',
      data: {
        title: parent ? 'Upload File to Accordion' : 'Upload File',
        service: this.api,
        questions$: this.accordionService.getCreateAccordionPage(),
        item: parent,
        isLink: false,
        id: parent?.id,
        accessUrl: environment.ASSET_BLOB_URL,
        containerName: environment.ASSET_CONTAINER,
        folderDataPath: '/WEBSITE_MANAGEMENT/AccordionFiles',
      },
    });

    dialogRef.componentInstance.formSubmit.subscribe((formData: FormData) => {
      if (parent?.id) this.expandedIds.add(parent.id);

      this.api.updateAccordionData(formData, parent?.id).subscribe({
        next: () => this.loadItems(true),
        error: (err) => console.error('File upload failed', err),
      });

      dialogRef.close();
    });

    dialogRef.componentInstance.cancel.subscribe(() => dialogRef.close());
  }

  private openAddLinkDialog(item: AccordionItem): void {
    const dialogRef = this.dialog.open(DynamicFileUploadComponent, {
      width: '420px',
      data: {
        title: 'External Link',
        service: this.api,
        questions$: this.accordionService.getLinkAccordionData(),
        item,
        id: item.id,
        isLink: true,
      },
    });

    dialogRef.componentInstance.formSubmit.subscribe((result: any) => {
      if (!result)
        return this.toast.showToast('warning', 'Please fill all link fields');

      const payload = {
        accordion_id: item.id,
        type: 'LINKS',
        links_list: result.links_list,
      };

      this.api.updateAccordionData(payload, item.id).subscribe({
        next: () => {
          item.id && this.expandedIds.add(item.id);
          this.loadItems(true);
        },
        error: (err) => {
          console.error('Link API failed', err);
        },
      });
    });
  }

  private openEditDialog(item: AccordionItem): void {
    console.log('-->', item);
    const dialogRef = this.dialog.open(DynamicPageCreationComponent, {
      width: '640px',
      data: {
        pageTitle: 'Edit Accordion',
        questions$: this.accordionService.getCreateAccordionPage(),
        initialData: item.data,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (!result) return;

      const payload = {
        ...result,
        id: item.id,
        data: (result.data ?? []).map((d: any) => ({
          data_type_id: String(d.id),
        })),
        website_id: this.websiteId,
        website_page_id: this.pageId,
      };

      if (item.id) this.expandedIds.add(item.id);

      this.api.updateAccordionData(payload, item.id).subscribe({
        next: () => this.loadItems(true),
        error: (err) => console.error('Update failed', err),
      });
    });
  }

  private confirmDelete(item: AccordionItem): void {
    if (
      !confirm('Delete this item and all its children? This cannot be undone.')
    )
      return;

    const isLink = item.type === 'links' || !!item.data?.accordion_details_id;
    const payload = isLink
      ? { accordion_details_id: item.data?.accordion_details_id ?? item.id }
      : { accordion_id: item.id };

    this.api.deleteAccordionData(payload).subscribe({
      next: () => this.loadItems(true),
      error: (err) => {
        console.error('Delete failed', err);
        alert('Could not delete item. Please try again.');
      },
    });
  }
}
